import {
  AxiosHeaders,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from "axios";
import apiClient, { requiresAuth } from "./apiClient";
import Course from "../models/Course";

class CourseService {
  async fetchCourses({
    type,
    page,
    limit,
    sortBy,
    sortOrder,
    search,
    visible,
  }: {
    type?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    search?: string;
    visible?: boolean;
  }) {
    try {
      const response = await apiClient.get("/courses", {
        params: {
          ...(type && { type }),
          ...(page && { page }),
          ...(limit && { limit }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
          ...(search && { search }),
          ...(visible && { visible }),
        },
      });

      return response.data;
    } catch (error) {
      console.error("There was an error retrieving courses:", error);
      throw error;
    }
  }

  async createCourse(courseData: FormData) {
    try {
      // Correctly construct the headers
      const headers = new AxiosHeaders({
        "Content-Type": "multipart/form-data",
      });

      const config: InternalAxiosRequestConfig = {
        headers: headers as AxiosRequestHeaders,
      };

      // Apply the helper function to add the requiresAuth header
      const finalConfig = requiresAuth(config);
      const response = await apiClient.post(
        "/courses",
        courseData,
        finalConfig
      );
      return response.data;
    } catch (error) {
      console.error("Error creating course:", error);
      throw error;
    }
  }

  async updateCourse(id: number, formData: FormData): Promise<Course> {
    // Correctly construct the headers
    const headers = new AxiosHeaders({
      "Content-Type": "multipart/form-data",
    });

    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
    };

    // Apply the helper function to add the requiresAuth header
    const finalConfig = requiresAuth(config);
    const response = await apiClient.put(
      `/courses/${id}`,
      formData,
      finalConfig
    );

    return response.data;
  }

  async archiveCourse(id: number): Promise<void> {
    await apiClient.delete(`/courses/${id}/archive`, requiresAuth());
  }

  async fetchArchivedCourses(
    page: number,
    rowsPerPage: number,
    orderBy: string,
    order: "asc" | "desc"
  ) {
    const headers = new AxiosHeaders({});

    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
      params: {
        page,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      },
    };

    try {
      const response = await apiClient.get(
        "/courses/archived",
        requiresAuth(config)
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching archived courses:", error);
      throw error;
    }
  }

  async restoreCourse(courseId: number) {
    try {
      const response = await apiClient.post(
        `/courses/${courseId}/restore`,
        null,
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Error restoring course:", error);
      throw error;
    }
  }

  async fetchCourseRoster(courseId: number) {
    try {
      const response = await apiClient.get(
        `/courses/${courseId}/roster`,
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching course roster:", error);
      throw error;
    }
  }

  async fetchParticipantsForCourse(courseId: number) {
    try {
      const response = await apiClient.get(
        `/courses/${courseId}/participants`,
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching participants for course:", error);
      throw error;
    }
  }

  async fetchCoursesForParticipant(participantId: string) {
    try {
      const response = await apiClient.get(
        `/participants/${participantId}/courses`,
        requiresAuth()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching courses for participant:", error);
      throw error;
    }
  }
}

export default new CourseService();
